import { useState, useEffect } from 'react';
import { supabase } from '../../supabase/client';

interface DemoRequest {
  id: string;
  name: string;
  email: string;
  company?: string;
  phone?: string;
  team_size?: string;
  message?: string;
  request_type: 'demo' | 'contact_sales';
  source_page: string;
  source_button?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  calendly_scheduled: boolean;
  calendly_event_url?: string;
  scheduled_time?: string;
  status: 'new' | 'contacted' | 'qualified' | 'scheduled' | 'completed' | 'closed';
  status_notes?: string;
  assigned_to?: string;
  created_at: string;
  updated_at: string;
  contacted_at?: string;
  completed_at?: string;
}

type StatusType = DemoRequest['status'];
type RequestType = DemoRequest['request_type'];

export default function AdminDemoRequests() {
  const [demoRequests, setDemoRequests] = useState<DemoRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<DemoRequest | null>(null);
  const [filterStatus, setFilterStatus] = useState<StatusType | 'all'>('all');
  const [filterType, setFilterType] = useState<RequestType | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  useEffect(() => {
    fetchDemoRequests();
  }, []);

  const fetchDemoRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('demo_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setDemoRequests(data || []);
    } catch (err) {
      console.error('Error fetching demo requests:', err);
      setError('Failed to load demo requests');
    } finally {
      setLoading(false);
    }
  };

  const updateRequestStatus = async (requestId: string, newStatus: StatusType, notes?: string) => {
    try {
      const updates: {
        status: StatusType;
        updated_at: string;
        status_notes?: string;
        contacted_at?: string;
        completed_at?: string;
      } = {
        status: newStatus,
        updated_at: new Date().toISOString()
      };

      if (notes) {
        updates.status_notes = notes;
      }

      if (newStatus === 'contacted') {
        updates.contacted_at = new Date().toISOString();
      } else if (newStatus === 'completed') {
        updates.completed_at = new Date().toISOString();
      }

      const { error: updateError } = await supabase
        .from('demo_requests')
        .update(updates)
        .eq('id', requestId);

      if (updateError) throw updateError;

      // Refresh the list
      await fetchDemoRequests();
      
      // Close modal if open
      setIsDetailModalOpen(false);
      setSelectedRequest(null);
    } catch (err) {
      console.error('Error updating request status:', err);
      alert('Failed to update status');
    }
  };

  const getStatusBadge = (status: StatusType) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    
    switch (status) {
      case 'new':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'contacted':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'qualified':
        return `${baseClasses} bg-purple-100 text-purple-800`;
      case 'scheduled':
        return `${baseClasses} bg-indigo-100 text-indigo-800`;
      case 'completed':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'closed':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getTypeBadge = (type: RequestType) => {
    const baseClasses = "inline-flex items-center px-2 py-1 rounded text-xs font-medium";
    return type === 'demo' 
      ? `${baseClasses} bg-green-50 text-green-700 border border-green-200`
      : `${baseClasses} bg-blue-50 text-blue-700 border border-blue-200`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTeamSize = (size?: string) => {
    if (!size) return 'Not specified';
    return size === '100+' ? '100+ employees' : `${size} employees`;
  };

  // Filter and search logic
  const filteredRequests = demoRequests.filter(request => {
    const matchesStatus = filterStatus === 'all' || request.status === filterStatus;
    const matchesType = filterType === 'all' || request.request_type === filterType;
    const matchesSearch = !searchTerm || 
      request.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (request.company && request.company.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesStatus && matchesType && matchesSearch;
  });

  // Statistics
  const stats = {
    total: demoRequests.length,
    new: demoRequests.filter(r => r.status === 'new').length,
    demos: demoRequests.filter(r => r.request_type === 'demo').length,
    sales: demoRequests.filter(r => r.request_type === 'contact_sales').length,
    scheduled: demoRequests.filter(r => r.calendly_scheduled).length
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Demo Requests</h2>
        <p className="text-gray-600 mt-1">
          Manage demo bookings and sales inquiries from the marketing website
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-500">Total Requests</div>
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-500">New</div>
          <div className="text-2xl font-bold text-blue-600">{stats.new}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-500">Demo Requests</div>
          <div className="text-2xl font-bold text-green-600">{stats.demos}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-500">Sales Inquiries</div>
          <div className="text-2xl font-bold text-blue-600">{stats.sales}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-500">Scheduled</div>
          <div className="text-2xl font-bold text-purple-600">{stats.scheduled}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Name, email, or company..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as StatusType | 'all')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="scheduled">Scheduled</option>
              <option value="completed">Completed</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as RequestType | 'all')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="demo">Demo Request</option>
              <option value="contact_sales">Sales Inquiry</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={fetchDemoRequests}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Scheduled
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                      <span className="text-gray-500">Loading demo requests...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredRequests.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="text-gray-500">
                      {searchTerm || filterStatus !== 'all' || filterType !== 'all' 
                        ? 'No requests found matching your filters.' 
                        : 'No demo requests yet.'}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{request.name}</div>
                        <div className="text-sm text-gray-500">{request.email}</div>
                        {request.phone && (
                          <div className="text-sm text-gray-500">{request.phone}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={getTypeBadge(request.request_type)}>
                        {request.request_type === 'demo' ? 'Demo' : 'Sales'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {request.company || 'Not specified'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatTeamSize(request.team_size)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={getStatusBadge(request.status)}>
                        {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {request.calendly_scheduled ? (
                        <div>
                          <span className="text-sm text-green-600 font-medium">✓ Scheduled</span>
                          {request.scheduled_time && (
                            <div className="text-xs text-gray-500">
                              {formatDate(request.scheduled_time)}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">Not scheduled</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(request.created_at)}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedRequest(request);
                          setIsDetailModalOpen(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        View
                      </button>
                      <select
                        value={request.status}
                        onChange={(e) => updateRequestStatus(request.id, e.target.value as StatusType)}
                        className="text-sm border border-gray-300 rounded px-2 py-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="qualified">Qualified</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="completed">Completed</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      {isDetailModalOpen && selectedRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Demo Request Details
                </h3>
                <button
                  onClick={() => {
                    setIsDetailModalOpen(false);
                    setSelectedRequest(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              <div className="space-y-4">
                {/* Contact Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Contact Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-500">Name:</span>
                      <p className="text-sm font-medium">{selectedRequest.name}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Email:</span>
                      <p className="text-sm font-medium">{selectedRequest.email}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Company:</span>
                      <p className="text-sm font-medium">{selectedRequest.company || 'Not specified'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Phone:</span>
                      <p className="text-sm font-medium">{selectedRequest.phone || 'Not provided'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Team Size:</span>
                      <p className="text-sm font-medium">{formatTeamSize(selectedRequest.team_size)}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Request Type:</span>
                      <p className="text-sm font-medium">
                        {selectedRequest.request_type === 'demo' ? 'Demo Request' : 'Sales Inquiry'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Message */}
                {selectedRequest.message && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Message</h4>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedRequest.message}</p>
                  </div>
                )}

                {/* Tracking Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Tracking Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-500">Source Page:</span>
                      <p className="text-sm font-medium">{selectedRequest.source_page}</p>
                    </div>
                    {selectedRequest.source_button && (
                      <div>
                        <span className="text-sm text-gray-500">Source Button:</span>
                        <p className="text-sm font-medium">{selectedRequest.source_button}</p>
                      </div>
                    )}
                    {selectedRequest.utm_source && (
                      <div>
                        <span className="text-sm text-gray-500">UTM Source:</span>
                        <p className="text-sm font-medium">{selectedRequest.utm_source}</p>
                      </div>
                    )}
                    {selectedRequest.utm_campaign && (
                      <div>
                        <span className="text-sm text-gray-500">UTM Campaign:</span>
                        <p className="text-sm font-medium">{selectedRequest.utm_campaign}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Calendly Information */}
                {selectedRequest.calendly_scheduled && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-green-700 mb-2">Calendly Booking</h4>
                    <div className="space-y-2">
                      {selectedRequest.scheduled_time && (
                        <p className="text-sm">
                          <span className="text-green-600">Scheduled for:</span>{' '}
                          <span className="font-medium">{formatDate(selectedRequest.scheduled_time)}</span>
                        </p>
                      )}
                      {selectedRequest.calendly_event_url && (
                        <p className="text-sm">
                          <a 
                            href={selectedRequest.calendly_event_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            View Calendly Event →
                          </a>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Status and Notes */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Status Management</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">Current Status</label>
                      <select
                        value={selectedRequest.status}
                        onChange={(e) => {
                          const newStatus = e.target.value as StatusType;
                          updateRequestStatus(selectedRequest.id, newStatus);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="qualified">Qualified</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="completed">Completed</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                    {selectedRequest.status_notes && (
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">Status Notes</label>
                        <p className="text-sm text-gray-900">{selectedRequest.status_notes}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Timestamps */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Timeline</h4>
                  <div className="space-y-1">
                    <p className="text-sm">
                      <span className="text-gray-500">Created:</span>{' '}
                      <span className="font-medium">{formatDate(selectedRequest.created_at)}</span>
                    </p>
                    {selectedRequest.contacted_at && (
                      <p className="text-sm">
                        <span className="text-gray-500">Contacted:</span>{' '}
                        <span className="font-medium">{formatDate(selectedRequest.contacted_at)}</span>
                      </p>
                    )}
                    {selectedRequest.completed_at && (
                      <p className="text-sm">
                        <span className="text-gray-500">Completed:</span>{' '}
                        <span className="font-medium">{formatDate(selectedRequest.completed_at)}</span>
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => {
                    setIsDetailModalOpen(false);
                    setSelectedRequest(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
}